import { BaseApiClient } from './base-client';
import { ChatApi } from './chat-api';
import { ImageApi } from './image-api';
import { VideoApi } from './video-api';
import { AudioApi } from './audio-api';
import { StorageApi } from './storage-api';
import { UserApi } from './user-api';
import { GalleryApi } from './gallery-api';
import { TelegramApi } from './telegram-api';
import { ComposerApi } from './composer-api';
import { CommunicationStylesApi } from './communication-styles-api';
import { TeamsApi } from './teams-api';
import { AvatarApi } from './avatar-api';
import { FileDeliveryApi } from './file-delivery-api';
import { WardrobeApi } from './wardrobe-api';
import { VideoRestyleApi } from './video-restyle-api';
import { MotionControlApi } from './motion-control-api';
import { UserElementsApi } from './user-elements-api';
import { UserAssistantsApi } from './user-assistants-api';
import { ModelStatusesApi } from './model-statuses-api';
import { AdminApi } from './admin-api';
import { PaymentApi } from './payment-api';
import { AchievementsApi } from './achievements-api';
import { ModelInfoApi } from './model-info-api';
import { GoogleAuthApi } from './google-auth-api';
import { ConsentApi } from './consent-api';
import { AccountLinkingApi } from './account-linking-api';
import { AccountMergeApi } from './account-merge-api';
import { VisionaryApi } from './visionary-api';
import type { ApiResponse } from '../../types';

/**
 * Unified API Client с модульной структурой
 * 
 * Использование:
 * - Новый способ (рекомендуется): apiClient.chat.sendChatMessage(...)
 * - Старый способ (обратная совместимость): apiClient.sendChatMessage(...)
 */
class ApiClient {
  // Модули API
  public chat: ChatApi;
  public image: ImageApi;
  public video: VideoApi;
  public audio: AudioApi;
  public storage: StorageApi;
  public user: UserApi;
  public gallery: GalleryApi;
  public telegram: TelegramApi;
  public composer: ComposerApi;
  public communicationStyles: CommunicationStylesApi;
  public teams: TeamsApi;
  public avatar: AvatarApi;
  public fileDelivery: FileDeliveryApi;
  public wardrobe: WardrobeApi;
  public videoRestyle: VideoRestyleApi;
  public motionControl: MotionControlApi;
  public userElements: UserElementsApi;
  public userAssistants: UserAssistantsApi;
  public modelStatuses: ModelStatusesApi;
  public admin: AdminApi;
  public payment: PaymentApi;
  public achievements: AchievementsApi;
  public modelInfo: ModelInfoApi;
  public googleAuth: GoogleAuthApi;
  public consent: ConsentApi;
  public accountLinking: AccountLinkingApi;
  public accountMerge: AccountMergeApi;
  public visionary: VisionaryApi;

  // Приватный клиент для health check
  private baseClient: BaseApiClient;

  // ===== BACKWARD COMPATIBILITY METHODS =====
  // Методы верхнего уровня для обратной совместимости
  
  // Chat API
  sendChatMessage!: typeof ChatApi.prototype.sendChatMessage;
  streamChatMessage!: typeof ChatApi.prototype.streamChatMessage;
  getChatSessions!: typeof ChatApi.prototype.getChatSessions;
  getGeneratingMessages!: typeof ChatApi.prototype.getGeneratingMessages;
  getArchivedChatSessions!: typeof ChatApi.prototype.getArchivedChatSessions;
  createChatSession!: typeof ChatApi.prototype.createChatSession;
  verifySecretChatPassword!: typeof ChatApi.prototype.verifySecretChatPassword;
  renameChatSession!: typeof ChatApi.prototype.renameChatSession;
  archiveChatSession!: typeof ChatApi.prototype.archiveChatSession;
  deleteChatSession!: typeof ChatApi.prototype.deleteChatSession;
  getChatSessionHistory!: typeof ChatApi.prototype.getChatSessionHistory;
  deleteChatMessage!: typeof ChatApi.prototype.deleteMessage;

  // Image API
  generateImages!: typeof ImageApi.prototype.generateImages;
  getHiggsfieldStyles!: typeof ImageApi.prototype.getHiggsfieldStyles;
  getHiggsfieldJobStatus!: typeof ImageApi.prototype.getHiggsfieldJobStatus;
  loadImageHistory!: typeof ImageApi.prototype.loadImageHistory;
  deleteImage!: typeof ImageApi.prototype.deleteImage;
  toggleImageFavorite!: typeof ImageApi.prototype.toggleImageFavorite;
  upscaleImage!: typeof ImageApi.prototype.upscaleImage;
  getUpscaleHistory!: typeof ImageApi.prototype.getUpscaleHistory;
  getFavoriteImages!: typeof ImageApi.prototype.getFavoriteImages;
  getFavoritePresets!: typeof ImageApi.prototype.getFavoritePresets;
  addFavoritePreset!: typeof ImageApi.prototype.addFavoritePreset;
  removeFavoritePreset!: typeof ImageApi.prototype.removeFavoritePreset;
  isPresetFavorite!: typeof ImageApi.prototype.isPresetFavorite;
  getActiveImageGenerations!: typeof ImageApi.prototype.getActiveImageGenerations;
  cancelImageGeneration!: typeof ImageApi.prototype.cancelImageGeneration;
  clearStuckImageGenerations!: typeof ImageApi.prototype.clearStuckImageGenerations;
  // Style Presets API
  getStylePresets!: typeof ImageApi.prototype.getStylePresets;
  getStylePresetPrompt!: typeof ImageApi.prototype.getStylePresetPrompt;
  adminGetAllStylePresets!: typeof ImageApi.prototype.adminGetAllStylePresets;
  adminCreateStylePreset!: typeof ImageApi.prototype.adminCreateStylePreset;
  adminUpdateStylePreset!: typeof ImageApi.prototype.adminUpdateStylePreset;
  adminDeleteStylePreset!: typeof ImageApi.prototype.adminDeleteStylePreset;
  // Image Edit (Inpaint) API
  editImage!: typeof ImageApi.prototype.editImage;
  getImageEditHistory!: typeof ImageApi.prototype.getImageEditHistory;
  // Image Edit iterations chain (desktop UX)
  getEditOriginals!: typeof ImageApi.prototype.getEditOriginals;
  getEditIterationsByRoot!: typeof ImageApi.prototype.getEditIterationsByRoot;
  saveCropIteration!: typeof ImageApi.prototype.saveCropIteration;
  retryIteration!: typeof ImageApi.prototype.retryIteration;
  updateIterationMask!: typeof ImageApi.prototype.updateIterationMask;
  deleteIteration!: typeof ImageApi.prototype.deleteIteration;
  deleteIterationChain!: typeof ImageApi.prototype.deleteIterationChain;
  // Image Edit sessions (migration 141)
  listEditSessions!: typeof ImageApi.prototype.listEditSessions;
  createEditSession!: typeof ImageApi.prototype.createEditSession;
  getEditSessionIterations!: typeof ImageApi.prototype.getEditSessionIterations;
  updateEditSessionDraft!: typeof ImageApi.prototype.updateEditSessionDraft;
  deleteEditSession!: typeof ImageApi.prototype.deleteEditSession;
  // Image Regenerate
  regenerateImage!: typeof ImageApi.prototype.regenerateImage;
  // Color Palettes
  getColorPalettes!: typeof ImageApi.prototype.getColorPalettes;
  createColorPalette!: typeof ImageApi.prototype.createColorPalette;
  deleteColorPalette!: typeof ImageApi.prototype.deleteColorPalette;

  // Video API
  generateVideo!: typeof VideoApi.prototype.generateVideo;
  retryVideoGeneration!: typeof VideoApi.prototype.retryVideoGeneration;
  getMotionPresets!: typeof VideoApi.prototype.getMotionPresets;
  remixSoraVideo!: typeof VideoApi.prototype.remixSoraVideo;
  listSoraVideos!: typeof VideoApi.prototype.listSoraVideos;
  deleteSoraVideo!: typeof VideoApi.prototype.deleteSoraVideo;
  downloadSoraVideo!: typeof VideoApi.prototype.downloadSoraVideo;
  loadVideoHistory!: typeof VideoApi.prototype.loadVideoHistory;
  deleteVideo!: typeof VideoApi.prototype.deleteVideo;
  toggleVideoFavorite!: typeof VideoApi.prototype.toggleVideoFavorite;
  getFavoriteVideos!: typeof VideoApi.prototype.getFavoriteVideos;
  getActiveVideoGenerations!: typeof VideoApi.prototype.getActiveVideoGenerations;
  cancelVideoGeneration!: typeof VideoApi.prototype.cancelVideoGeneration;
  clearStuckVideoGenerations!: typeof VideoApi.prototype.clearStuckVideoGenerations;

  // Audio API
  transcribeAudio!: typeof AudioApi.prototype.transcribeAudio;
  textToSpeech!: typeof AudioApi.prototype.textToSpeech;
  streamTranscription!: typeof AudioApi.prototype.streamTranscription;

  // Storage API
  deleteFromS3!: typeof StorageApi.prototype.deleteFromS3;
  copyFileInS3!: typeof StorageApi.prototype.copyFileInS3;
  uploadFileToS3!: typeof StorageApi.prototype.uploadFileToS3;

  // User API
  getUserInfo!: typeof UserApi.prototype.getUserInfo;
  updateUserTokens!: typeof UserApi.prototype.updateUserTokens;
  updateUserSettings!: typeof UserApi.prototype.updateUserSettings;

  // Gallery API
  getGalleryItems!: typeof GalleryApi.prototype.getGalleryItems;
  deleteGalleryItem!: typeof GalleryApi.prototype.deleteGalleryItem;
  deleteGalleryItems!: typeof GalleryApi.prototype.deleteGalleryItems;
  toggleGalleryItemFavorite!: typeof GalleryApi.prototype.toggleGalleryItemFavorite;
  createGalleryFolder!: typeof GalleryApi.prototype.createGalleryFolder;
  deleteGalleryFolder!: typeof GalleryApi.prototype.deleteGalleryFolder;
  renameGalleryFolder!: typeof GalleryApi.prototype.renameGalleryFolder;
  moveGalleryItems!: typeof GalleryApi.prototype.moveGalleryItems;
  downloadZip!: typeof GalleryApi.prototype.downloadZip;

  // Telegram API (legacy - используйте fileDelivery)
  sendVideoToChat!: typeof TelegramApi.prototype.sendVideoToChat;
  sendImageToChat!: typeof TelegramApi.prototype.sendImageToChat;
  sendFileToChat!: typeof TelegramApi.prototype.sendFileToChat;
  sendMediaGroupToChat!: typeof TelegramApi.prototype.sendMediaGroupToChat;

  // File Delivery API (новый унифицированный сервис)
  deliverFile!: typeof FileDeliveryApi.prototype.deliverFile;
  deliverImage!: typeof FileDeliveryApi.prototype.deliverImage;
  deliverVideo!: typeof FileDeliveryApi.prototype.deliverVideo;
  deliverBatch!: typeof FileDeliveryApi.prototype.deliverBatch;

  // Composer API
  saveChatComposerAttachment!: typeof ComposerApi.prototype.saveChatComposerAttachment;
  getChatComposerAttachments!: typeof ComposerApi.prototype.getChatComposerAttachments;
  deleteChatComposerAttachment!: typeof ComposerApi.prototype.deleteChatComposerAttachment;
  clearChatComposerAttachments!: typeof ComposerApi.prototype.clearChatComposerAttachments;
  saveImageComposerAttachment!: typeof ComposerApi.prototype.saveImageComposerAttachment;
  getImageComposerAttachments!: typeof ComposerApi.prototype.getImageComposerAttachments;
  deleteImageComposerAttachment!: typeof ComposerApi.prototype.deleteImageComposerAttachment;
  clearImageComposerAttachments!: typeof ComposerApi.prototype.clearImageComposerAttachments;
  saveVideoComposerAttachment!: typeof ComposerApi.prototype.saveVideoComposerAttachment;
  getVideoComposerAttachments!: typeof ComposerApi.prototype.getVideoComposerAttachments;
  deleteVideoComposerAttachment!: typeof ComposerApi.prototype.deleteVideoComposerAttachment;
  clearVideoComposerAttachments!: typeof ComposerApi.prototype.clearVideoComposerAttachments;

  constructor() {
    // Инициализируем все модули
    this.chat = new ChatApi();
    this.image = new ImageApi();
    this.video = new VideoApi();
    this.audio = new AudioApi();
    this.storage = new StorageApi();
    this.user = new UserApi();
    this.gallery = new GalleryApi();
    this.telegram = new TelegramApi();
    this.composer = new ComposerApi();
    this.communicationStyles = new CommunicationStylesApi();
    this.teams = new TeamsApi();
    this.avatar = new AvatarApi();
    this.fileDelivery = new FileDeliveryApi();
    this.wardrobe = new WardrobeApi();
    this.videoRestyle = new VideoRestyleApi();
    this.motionControl = new MotionControlApi();
    this.userElements = new UserElementsApi();
    this.userAssistants = new UserAssistantsApi();
    this.modelStatuses = new ModelStatusesApi();
    this.admin = new AdminApi();
    this.payment = new PaymentApi();
    this.achievements = new AchievementsApi();
    this.modelInfo = new ModelInfoApi();
    this.googleAuth = new GoogleAuthApi();
    this.consent = new ConsentApi();
    this.accountLinking = new AccountLinkingApi();
    this.accountMerge = new AccountMergeApi();
    this.visionary = new VisionaryApi();
    this.baseClient = new BaseApiClient();

    // Привязываем методы для обратной совместимости
    // Chat API
    this.sendChatMessage = this.chat.sendChatMessage.bind(this.chat);
    this.streamChatMessage = this.chat.streamChatMessage.bind(this.chat);
    this.getChatSessions = this.chat.getChatSessions.bind(this.chat);
    this.getGeneratingMessages = this.chat.getGeneratingMessages.bind(this.chat);
    this.getArchivedChatSessions = this.chat.getArchivedChatSessions.bind(this.chat);
    this.createChatSession = this.chat.createChatSession.bind(this.chat);
    this.verifySecretChatPassword = this.chat.verifySecretChatPassword.bind(this.chat);
    this.renameChatSession = this.chat.renameChatSession.bind(this.chat);
    this.archiveChatSession = this.chat.archiveChatSession.bind(this.chat);
    this.deleteChatSession = this.chat.deleteChatSession.bind(this.chat);
    this.getChatSessionHistory = this.chat.getChatSessionHistory.bind(this.chat);
    this.deleteChatMessage = this.chat.deleteMessage.bind(this.chat);

    // Image API
    this.generateImages = this.image.generateImages.bind(this.image);
    this.getHiggsfieldStyles = this.image.getHiggsfieldStyles.bind(this.image);
    this.getHiggsfieldJobStatus = this.image.getHiggsfieldJobStatus.bind(this.image);
    this.loadImageHistory = this.image.loadImageHistory.bind(this.image);
    this.deleteImage = this.image.deleteImage.bind(this.image);
    this.toggleImageFavorite = this.image.toggleImageFavorite.bind(this.image);
    this.upscaleImage = this.image.upscaleImage.bind(this.image);
    this.getUpscaleHistory = this.image.getUpscaleHistory.bind(this.image);
    this.getFavoriteImages = this.image.getFavoriteImages.bind(this.image);
    this.getFavoritePresets = this.image.getFavoritePresets.bind(this.image);
    this.addFavoritePreset = this.image.addFavoritePreset.bind(this.image);
    this.removeFavoritePreset = this.image.removeFavoritePreset.bind(this.image);
    this.isPresetFavorite = this.image.isPresetFavorite.bind(this.image);
    this.getActiveImageGenerations = this.image.getActiveImageGenerations.bind(this.image);
    this.cancelImageGeneration = this.image.cancelImageGeneration.bind(this.image);
    this.clearStuckImageGenerations = this.image.clearStuckImageGenerations.bind(this.image);
    // Style Presets API
    this.getStylePresets = this.image.getStylePresets.bind(this.image);
    this.getStylePresetPrompt = this.image.getStylePresetPrompt.bind(this.image);
    this.adminGetAllStylePresets = this.image.adminGetAllStylePresets.bind(this.image);
    this.adminCreateStylePreset = this.image.adminCreateStylePreset.bind(this.image);
    this.adminUpdateStylePreset = this.image.adminUpdateStylePreset.bind(this.image);
    this.adminDeleteStylePreset = this.image.adminDeleteStylePreset.bind(this.image);
    // Image Edit (Inpaint) API
    this.editImage = this.image.editImage.bind(this.image);
    this.getImageEditHistory = this.image.getImageEditHistory.bind(this.image);
    // Image Edit iterations chain
    this.getEditOriginals = this.image.getEditOriginals.bind(this.image);
    this.getEditIterationsByRoot = this.image.getEditIterationsByRoot.bind(this.image);
    this.saveCropIteration = this.image.saveCropIteration.bind(this.image);
    this.retryIteration = this.image.retryIteration.bind(this.image);
    this.updateIterationMask = this.image.updateIterationMask.bind(this.image);
    this.deleteIteration = this.image.deleteIteration.bind(this.image);
    this.deleteIterationChain = this.image.deleteIterationChain.bind(this.image);
    this.listEditSessions = this.image.listEditSessions.bind(this.image);
    this.createEditSession = this.image.createEditSession.bind(this.image);
    this.getEditSessionIterations = this.image.getEditSessionIterations.bind(this.image);
    this.updateEditSessionDraft = this.image.updateEditSessionDraft.bind(this.image);
    this.deleteEditSession = this.image.deleteEditSession.bind(this.image);
    this.regenerateImage = this.image.regenerateImage.bind(this.image);
    this.getColorPalettes = this.image.getColorPalettes.bind(this.image);
    this.createColorPalette = this.image.createColorPalette.bind(this.image);
    this.deleteColorPalette = this.image.deleteColorPalette.bind(this.image);

    // Video API
    this.generateVideo = this.video.generateVideo.bind(this.video);
    this.retryVideoGeneration = this.video.retryVideoGeneration.bind(this.video);
    this.getMotionPresets = this.video.getMotionPresets.bind(this.video);
    this.remixSoraVideo = this.video.remixSoraVideo.bind(this.video);
    this.listSoraVideos = this.video.listSoraVideos.bind(this.video);
    this.deleteSoraVideo = this.video.deleteSoraVideo.bind(this.video);
    this.downloadSoraVideo = this.video.downloadSoraVideo.bind(this.video);
    this.loadVideoHistory = this.video.loadVideoHistory.bind(this.video);
    this.deleteVideo = this.video.deleteVideo.bind(this.video);
    this.toggleVideoFavorite = this.video.toggleVideoFavorite.bind(this.video);
    this.getFavoriteVideos = this.video.getFavoriteVideos.bind(this.video);
    this.getActiveVideoGenerations = this.video.getActiveVideoGenerations.bind(this.video);
    this.cancelVideoGeneration = this.video.cancelVideoGeneration.bind(this.video);
    this.clearStuckVideoGenerations = this.video.clearStuckVideoGenerations.bind(this.video);

    // Audio API
    this.transcribeAudio = this.audio.transcribeAudio.bind(this.audio);
    this.textToSpeech = this.audio.textToSpeech.bind(this.audio);
    this.streamTranscription = this.audio.streamTranscription.bind(this.audio);

    // Storage API
    this.deleteFromS3 = this.storage.deleteFromS3.bind(this.storage);
    this.copyFileInS3 = this.storage.copyFileInS3.bind(this.storage);
    this.uploadFileToS3 = this.storage.uploadFileToS3.bind(this.storage);

    // User API
    this.getUserInfo = this.user.getUserInfo.bind(this.user);
    this.updateUserTokens = this.user.updateUserTokens.bind(this.user);
    this.updateUserSettings = this.user.updateUserSettings.bind(this.user);

    // Gallery API
    this.getGalleryItems = this.gallery.getGalleryItems.bind(this.gallery);
    this.deleteGalleryItem = this.gallery.deleteGalleryItem.bind(this.gallery);
    this.deleteGalleryItems = this.gallery.deleteGalleryItems.bind(this.gallery);
    this.toggleGalleryItemFavorite = this.gallery.toggleGalleryItemFavorite.bind(this.gallery);
    this.createGalleryFolder = this.gallery.createGalleryFolder.bind(this.gallery);
    this.deleteGalleryFolder = this.gallery.deleteGalleryFolder.bind(this.gallery);
    this.renameGalleryFolder = this.gallery.renameGalleryFolder.bind(this.gallery);
    this.moveGalleryItems = this.gallery.moveGalleryItems.bind(this.gallery);
    this.downloadZip = this.gallery.downloadZip.bind(this.gallery);

    // Telegram API (legacy - используйте fileDelivery)
    this.sendVideoToChat = this.telegram.sendVideoToChat.bind(this.telegram);
    this.sendImageToChat = this.telegram.sendImageToChat.bind(this.telegram);
    this.sendFileToChat = this.telegram.sendFileToChat.bind(this.telegram);
    this.sendMediaGroupToChat = this.telegram.sendMediaGroupToChat.bind(this.telegram);

    // File Delivery API (новый унифицированный сервис)
    this.deliverFile = this.fileDelivery.deliverFile.bind(this.fileDelivery);
    this.deliverImage = this.fileDelivery.deliverImage.bind(this.fileDelivery);
    this.deliverVideo = this.fileDelivery.deliverVideo.bind(this.fileDelivery);
    this.deliverBatch = this.fileDelivery.deliverBatch.bind(this.fileDelivery);

    // Composer API
    this.saveChatComposerAttachment = this.composer.saveChatComposerAttachment.bind(this.composer);
    this.getChatComposerAttachments = this.composer.getChatComposerAttachments.bind(this.composer);
    this.deleteChatComposerAttachment = this.composer.deleteChatComposerAttachment.bind(this.composer);
    this.clearChatComposerAttachments = this.composer.clearChatComposerAttachments.bind(this.composer);
    this.saveImageComposerAttachment = this.composer.saveImageComposerAttachment.bind(this.composer);
    this.getImageComposerAttachments = this.composer.getImageComposerAttachments.bind(this.composer);
    this.deleteImageComposerAttachment = this.composer.deleteImageComposerAttachment.bind(this.composer);
    this.clearImageComposerAttachments = this.composer.clearImageComposerAttachments.bind(this.composer);
    this.saveVideoComposerAttachment = this.composer.saveVideoComposerAttachment.bind(this.composer);
    this.getVideoComposerAttachments = this.composer.getVideoComposerAttachments.bind(this.composer);
    this.deleteVideoComposerAttachment = this.composer.deleteVideoComposerAttachment.bind(this.composer);
    this.clearVideoComposerAttachments = this.composer.clearVideoComposerAttachments.bind(this.composer);
  }

  // ===== HEALTH CHECK =====
  
  async healthCheck(): Promise<ApiResponse> {
    const response = await (this.baseClient as any).client.get('/health');
    return response.data;
  }
}

// Экспортируем singleton инстанс
export const apiClient = new ApiClient();
export default apiClient;

// Экспортируем также отдельные классы для прямого использования (если нужно)
export { ChatApi } from './chat-api';
export { ImageApi, type ImageStylePreset, type ImageStylePresetAdmin } from './image-api';
export { VideoApi } from './video-api';
export { AudioApi } from './audio-api';
export { StorageApi } from './storage-api';
export { UserApi } from './user-api';
export { GalleryApi } from './gallery-api';
export { TelegramApi } from './telegram-api';
export { ComposerApi } from './composer-api';
export { CommunicationStylesApi } from './communication-styles-api';
export { TeamsApi } from './teams-api';
export type { Team, TeamMember, TeamFolder, TeamTokenTransaction, TeamStats, TeamInvite, TeamInviteInfo } from './teams-api';
export { AvatarApi } from './avatar-api';
export type { 
  TtsGenerateParams, TtsGenerateResponse, 
  AvatarGenerateParams, AvatarGenerateResponse, 
  GeneratedAvatar, AvatarHistoryResponse 
} from './avatar-api';
export { FileDeliveryApi } from './file-delivery-api';
export type { FileType, DeliveryOptions, BatchDeliveryItem, DeliveryResult } from './file-delivery-api';
export { WardrobeApi, wardrobeApi } from './wardrobe-api';
export { ModelStatusesApi } from './model-statuses-api';
export type { ModelStatus, UpsertModelStatusParams } from './model-statuses-api';
export { BaseApiClient, API_BASE_URL } from './base-client';
export { AdminApi } from './admin-api';
export { PaymentApi } from './payment-api';
export { AchievementsApi } from './achievements-api';
export { ModelInfoApi } from './model-info-api';
export { MotionControlApi, motionControlApi } from './motion-control-api';
export { VisionaryApi, visionaryApi } from './visionary-api';
export type { VisionaryGenerateParams, VisionaryGeneratedImage, VisionaryGenerateResponse, VisionaryHistoryResponse } from './visionary-api';
export type { 
  MotionControlAttachment, 
  MotionControlAttachmentsResponse,
  MotionControlGenerateResponse,
  MotionControlPollResponse,
  MotionControlHistoryVideo,
  MotionControlHistoryResponse,
  ActiveMotionControlGeneration
} from './motion-control-api';

